import type {APIGuild, APIGuildInvite} from '@app/common/api'
import {useGuildInvites} from '@app/common/api'
import {Avatar} from '@app/components/Avatar/Avatar'
import {Modal, ModalBody, ModalFooter} from '@app/components/Modal/Modal'
import {Spinner} from '@app/components/Spinner/Spinner'
import {Tooltip} from '@app/components/Tooltip/Tooltip'
import {api} from '@app/hooks/useApi'
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  List,
  Stack,
  Text,
} from '@chakra-ui/react'
import {QuestionMarkCircleIcon, XMarkIcon} from '@heroicons/react/24/solid'
import {useQueryClient} from '@tanstack/react-query'
import axios from 'axios'
import React from 'react'
import toast from 'react-hot-toast'
import {useNavigate} from 'react-router-dom'

type ModalProps = {
  guild: APIGuild
  buttonRef?: any
  isOpen: boolean
  onClose(): void
}

export function GuildInvitesModal(props: ModalProps): React.JSX.Element | null {
  const [value, setValue] = React.useState('')
  const [error, setError] = React.useState('')
  const [isLoading, setIsLoading] = React.useState(false)
  const {data, status} = useGuildInvites(props.guild.name, props.isOpen)
  const queryClient = useQueryClient()

  return (
    <Modal
      finalFocusRef={props.buttonRef}
      footer={
        <ModalFooter>
          <FormControl as="form" isInvalid={Boolean(error)}>
            <InputGroup size="md">
              <Input
                autoFocus
                fontSize="sm"
                max={255}
                min={1}
                onChange={event => setValue(event.target.value)}
                placeholder="Enter Player Name"
                pr="4.5rem"
                value={value}
              />
              <InputRightElement w="4.5rem">
                <Button
                  h="1.75rem"
                  isLoading={isLoading}
                  onClick={async event => {
                    event.preventDefault()
                    if (!value) {
                      setError('This field is required.')
                      return
                    }

                    setIsLoading(true)
                    try {
                      await api.put(`/guilds/${props.guild.name}/invites/${value}`)
                      setValue('')
                      setError('')
                      await queryClient.invalidateQueries({queryKey: ['guilds', props.guild.name, 'invites']})
                    } catch (error) {
                      if (axios.isAxiosError(error))
                        setError(error.response?.data.message ?? 'Something went wrong, please try again.')
                    }

                    setIsLoading(false)
                  }}
                  size="sm"
                  type="submit"
                >
                  Invite
                </Button>
              </InputRightElement>
            </InputGroup>
            <FormErrorMessage>{error}</FormErrorMessage>
          </FormControl>
        </ModalFooter>
      }
      header="Guild Invites"
      isOpen={props.isOpen}
      onClose={props.onClose}
    >
      <ModalBody fontSize="md">
        {status === 'pending' || !data ? (
          <Spinner />
        ) : data.length === 0 ? (
          <Stack align="center" h="full" justify="center" py={16} textAlign="center">
            <QuestionMarkCircleIcon color="var(--chakra-colors-orange-200)" height={75} width={75} />
            <Heading fontSize="2xl" fontWeight="bold">
              *cricket noises*
            </Heading>
            <Text color="whiteAlpha.800" fontSize="md">
              No one has been invited to this guild yet.
            </Text>
          </Stack>
        ) : (
          <List spacing={2} userSelect="text">
            {data.map(invite => (
              <PlayerEntry guild={props.guild} invite={invite} key={invite.id} onClose={props.onClose} />
            ))}
          </List>
        )}
      </ModalBody>
    </Modal>
  )
}

function PlayerEntry({
  guild,
  invite,
  onClose,
}: {
  guild: APIGuild
  invite: APIGuildInvite
  onClose(): void
}): React.JSX.Element {
  const [isLoading, setIsLoading] = React.useState(false)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  return (
    <Flex justify="space-between">
      <Flex align="center">
        <Avatar name={invite.invitee.name} shouldShowOnline={false} size={32} skinHash={invite.invitee.skinHash} />
        <Box ml={3}>
          <Stack align="center" direction="row">
            <Link
              onClick={event => {
                if (event.metaKey || event.ctrlKey) {
                  window.open(`/player/${invite.invitee.name}`, '_blank')
                } else {
                  navigate(`/player/${invite.invitee.name}`)
                  onClose()
                }
              }}
            >
              <Heading fontSize="md">{invite.invitee.name}</Heading>
            </Link>
          </Stack>
          <Text color="gray.400" fontSize="sm">
            Invited by <strong>{invite.inviter.name}</strong>
          </Text>
        </Box>
      </Flex>

      <Stack direction="row">
        <Tooltip label="Revoke Invite">
          <IconButton
            aria-label="Revoke Invite"
            colorScheme="red"
            icon={<XMarkIcon height={16} width={16} />}
            isLoading={isLoading}
            onClick={async () => {
              setIsLoading(true)
              try {
                await api.delete(`/guilds/${guild.name}/invites/${invite.id}`)
                await queryClient.invalidateQueries({queryKey: ['guilds', guild.name, 'invites']})
              } catch {
                toast.error('Something went wrong! Please try again later.')
              }

              setIsLoading(false)
            }}
            rounded="full"
            size="sm"
          />
        </Tooltip>
      </Stack>
    </Flex>
  )
}
